import { roleAtLeast, type Role } from '@gitrader/shared';
import type { RouteNode, RouteStatus, RouteTree, TradingEnv } from './types';

/** A node with everything it inherits from its ancestors resolved. */
export interface ResolvedRoute {
  node: RouteNode;
  /** Absolute path, '/' for the root. */
  fullPath: string;
  /** Ancestor chain including the node itself, root first — the breadcrumb. */
  chain: readonly RouteNode[];
  env: TradingEnv | undefined;
  status: RouteStatus;
  minRole: Role | undefined;
  /** True when the node has no `page` and no children: the Placeholder renders. */
  placeholder: boolean;
}

export function joinPath(segments: readonly string[]): string {
  const clean = segments.filter((s) => s !== '');
  return clean.length === 0 ? '/' : `/${clean.join('/')}`;
}

/** Every node in the tree, depth-first, with inherited fields resolved. */
export function flatten(tree: RouteTree | readonly RouteNode[]): ResolvedRoute[] {
  const out: ResolvedRoute[] = [];
  const walk = (nodes: readonly RouteNode[], chain: readonly RouteNode[]): void => {
    for (const node of nodes) {
      const next = [...chain, node];
      out.push(resolveChain(next));
      if (node.children) walk(node.children, next);
    }
  };
  walk(tree, []);
  return out;
}

function resolveChain(chain: readonly RouteNode[]): ResolvedRoute {
  const node = chain[chain.length - 1]!;
  let env: TradingEnv | undefined;
  let status: RouteStatus = 'ready';
  let minRole: Role | undefined;
  for (const n of chain) {
    if (n.env) env = n.env;
    if (n.status === 'soon') status = 'soon';
    if (n.minRole && (!minRole || roleAtLeast(n.minRole, minRole))) minRole = n.minRole;
  }
  return {
    node,
    fullPath: joinPath(chain.map((n) => n.path)),
    chain,
    env,
    status,
    minRole,
    placeholder: !node.page && !node.children?.length,
  };
}

/** The resolved route for an exact pathname, or undefined. Trailing slashes are ignored. */
export function resolve(tree: RouteTree, pathname: string): ResolvedRoute | undefined {
  const target = normalize(pathname);
  const all = flatten(tree);
  return (
    all.find((r) => r.fullPath === target) ?? all.find((r) => matchesPattern(r.fullPath, target))
  );
}

/** '/data/jobs/:id' matches '/data/jobs/1234' — one dynamic segment per `:name` part. */
function matchesPattern(pattern: string, target: string): boolean {
  if (!pattern.includes('/:')) return false;
  const p = pattern.split('/');
  const t = target.split('/');
  if (p.length !== t.length) return false;
  return p.every((seg, i) => (seg.startsWith(':') ? t[i] !== '' : seg === t[i]));
}

export function resolveById(tree: RouteTree, id: string): ResolvedRoute | undefined {
  return flatten(tree).find((r) => r.node.id === id);
}

function normalize(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** Where a section lands: its first leaf that `role` may see, depth-first. */
export function firstLeaf(resolved: ResolvedRoute, role: Role): ResolvedRoute | undefined {
  if (!resolved.node.children?.length || resolved.node.page) return resolved;
  for (const child of resolved.node.children) {
    const r = resolveChain([...resolved.chain, child]);
    if (!allowed(r, role) || child.hidden) continue;
    const leaf = firstLeaf(r, role);
    if (leaf) return leaf;
  }
  return undefined;
}

export function allowed(resolved: ResolvedRoute, role: Role): boolean {
  return !resolved.minRole || roleAtLeast(role, resolved.minRole);
}

/**
 * The tree pruned to what `role` may see and what belongs in a nav surface.
 * Hidden nodes are dropped; roles below `minRole` never see the entry.
 */
export function visibleFor<T extends RouteNode>(nodes: readonly T[], role: Role): T[] {
  return nodes
    .filter((n) => !n.hidden && (!n.minRole || roleAtLeast(role, n.minRole)))
    .map((n) => (n.children ? { ...n, children: visibleFor(n.children, role) } : n));
}

/** Ids of the nodes on the way to `pathname`, the deepest match if the path is unknown. */
export function activeChain(tree: RouteTree, pathname: string): readonly RouteNode[] {
  const exact = resolve(tree, pathname);
  if (exact) return exact.chain;
  // Unknown path: fall back to the longest known prefix so the nav still shows a section.
  const target = normalize(pathname);
  let best: ResolvedRoute | undefined;
  for (const r of flatten(tree)) {
    if (r.fullPath !== '/' && target.startsWith(`${r.fullPath}/`)) {
      if (!best || r.fullPath.length > best.fullPath.length) best = r;
    }
  }
  return best?.chain ?? [];
}
